/**
 * Copyright Highway9 Networks Inc.
 *
 * Default Channel Pool configuration seeded for every Radio Site
 * after VLC upgrade. Matches the post-upgrade behaviour in the spec:
 *  - Pool 1: 20 MHz CA pairs (non-border uses the SAS-Optimized layout)
 *  - Pool 2: 10 MHz CA pairs (non-border uses the SAS-Optimized layout)
 */
import {
  ChannelPoolItem,
  ChannelRadioConfig,
  SiteChannelPool,
} from "~/types/channelPool";

// Default 20 MHz pairs agreed with backend (MCS-3637 + MCS-3713). Each pair
// is a CA pair (Primary + Secondary).
const default20MHzBorder: ChannelRadioConfig = {
  band: "B48",
  bandwidth: 20,
  channelGroups: [[56340, 56540]],
};

// Non-border defaults use the SAS-Optimized pairing (MCS-3906): the highest
// channels are paired with the lowest (7-1, 6-2, 5-3) so radios keep a
// working primary even when the SAS revokes the low channels. These values
// must stay in sync with the SAS_OPTIMIZED tables in
// views/radios/Sites/Form/Frequency/channelPairings.ts.
const default20MHzNonBorder: ChannelRadioConfig = {
  band: "B48",
  bandwidth: 20,
  // Channel pairs 7-1, 6-2, 5-3
  channelGroups: [
    [56540, 55340],
    [56340, 55540],
    [56140, 55740],
  ],
};

const default10MHzBorder: ChannelRadioConfig = {
  band: "B48",
  bandwidth: 10,
  channelGroups: [[56590, 56690]],
};

const default10MHzNonBorder: ChannelRadioConfig = {
  band: "B48",
  bandwidth: 10,
  // SAS-Optimized channel pairs 15-1, 14-2 .. 9-7 (channel 8 is unused).
  channelGroups: [
    [56690, 55290],
    [56590, 55390],
    [56490, 55490],
    [56390, 55590],
    [56290, 55690],
    [56190, 55790],
    [56090, 55890],
  ],
};

export const defaultPool20MHz: ChannelPoolItem = {
  borderRadioConfig: default20MHzBorder,
  nonBorderRadioConfig: default20MHzNonBorder,
};

export const defaultPool10MHz: ChannelPoolItem = {
  borderRadioConfig: default10MHzBorder,
  nonBorderRadioConfig: default10MHzNonBorder,
};

export const defaultSiteChannelPool: SiteChannelPool = {
  channelPool: [defaultPool20MHz, defaultPool10MHz],
  borderRadioPciRange: { start: 450, end: 503 },
  nonBorderRadioPciRange: { start: 0, end: 449 },
};

/**
 * A brand-new pool added via the "+ Add Channel Pool" button.
 * Defaults to 20 MHz B48 with one empty group on each side so the
 * user can immediately configure channels without extra clicks.
 */
export const newEmptyPool = (): ChannelPoolItem => ({
  borderRadioConfig: {
    band: "B48",
    bandwidth: 20,
    channelGroups: [[""]],
  },
  nonBorderRadioConfig: {
    band: "B48",
    bandwidth: 20,
    channelGroups: [[""]],
  },
});
